/**
 * How inbound candidate questions were answered BEFORE this tab existed.
 *
 * These are measured values, not estimates. Source: the reply exports taken from four recruiter
 * mailboxes before that export panel was retired — 712 question→reply pairs sent between 18 Jan and
 * 11 Aug 2026, timestamped from Gmail on both ends (the candidate's message, the recruiter's reply).
 *
 * WHY IT IS HARD-CODED: the corpus it came from is gone from the product on purpose. There is no
 * live query that can reproduce these numbers, and there never will be — which is exactly why they
 * are worth keeping. A number nobody can recompute is one nobody should silently edit either: if
 * these change, they change because the corpus was re-measured, and the note here says so.
 */

/** Baseline figures. Durations in hours, rates as fractions. */
export const RESPONSE_BASELINE = {
  /** Candidate's message → recruiter's reply, wall clock. */
  medianReplyHours: 14.4,
  /** p90 wall clock — 3.8 days. The tail is the part candidates talk about. */
  p90ReplyHours: 91.2,
  /**
   * The same median counting only Mo–Fr 09:00–18:00 Berlin. Kept even though the dashboard has no
   * business-hours figure to sit beside: it is the honest read of how fast the team actually was,
   * and it stops the wall-clock number being mistaken for slowness.
   */
  medianBusinessHours: 3.8,
  /** Share answered inside 24 h, wall clock. */
  withinDayRate: 0.676,
  /**
   * Share of threads carrying a real candidate question that ended with NO reply at all — 162 of
   * 438. Not the same as the raw "unanswered" count in the export, which is three-quarters
   * recruiter outbound mail with no question behind it.
   */
  unansweredRate: 0.37,
  /** Sample size and shape, so the figures can be judged rather than just quoted. */
  pairs: 712,
  mailboxes: 4,
  period: 'Jan–Aug 2026',
} as const;

/**
 * Fewest observed sends before a ratio may be shown at all.
 *
 * Not a style choice. Coverage of the live reply figure is partial by construction, so a range can
 * easily hold a handful of observations — and a median of five against a baseline of 712 produces
 * numbers like "28.8× faster" that are pure noise and look like a result. Below this, the baseline
 * is still shown; the comparison is not.
 */
export const MIN_OBSERVATIONS_TO_COMPARE = 30;

/**
 * How the live duration compares with the baseline one.
 *
 * Returns `null` when there is nothing to compare — no live value yet, or either side at zero,
 * where a ratio would be meaningless or infinite. Callers render the baseline alone in that case
 * rather than inventing a comparison.
 */
export function compareDuration(
  baselineHours: number,
  liveHours: number | null
): { factor: number; faster: boolean } | null {
  if (liveHours === null || !Number.isFinite(liveHours)) return null;
  if (liveHours <= 0 || baselineHours <= 0) return null;
  const faster = liveHours < baselineHours;
  const factor = faster ? baselineHours / liveHours : liveHours / baselineHours;
  // Under a tenth of a turn either way is noise on a median of a few hundred samples, and "1.0×
  // faster" reads as a finding when it is a rounding artefact.
  if (factor < 1.1) return null;
  return { factor: Math.round(factor * 10) / 10, faster };
}
